import { TMSOrderType, TMSOrderVo } from "@/types/tms";
import SearchForm from "@/components/shared/SearchForm";
import {
  DialogRef,
  IntegrationLogSearchParams,
  IntegrationOrderSearchParams,
  IntegrationOrderType,
  IntegrationOrderVo,
  SearchItem,
} from "@/types";
import { getDefaultSorts, usePageQuery } from "@/hooks/shared/query";
import {
  IntegrationOrderPageSearchItems,
  IntegrationOrderPageTabs,
  getIntegrationOrderAssociatedTMSOrder,
} from "./helper";
import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { wrapPage } from "@/utils/pages";
import PageHeader from "@/components/shared/PageHeader";
import { StringParam, withDefault } from "use-query-params";
import { useDebounce } from "@/hooks/shared/debounce";
import IntegrationOrdersTable from "@/components/integration/order/OrderTable";
import { IntegrationOrderCols } from "@/modal/integration/order/table";
import CommonTabs from "@/components/shared/CommonTabs";
import {
  IntegrationLogCols,
  IntegrationLogTableSearchItems,
} from "@/modal/integration/log/table";
import IntegrationLogsTable from "@/components/integration/log/LogTable";
import LinkColumn from "@/components/shared/LinkColumn";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import { Button, Empty, Modal } from "antd";
import { useGlobalContext } from "@/hooks/context";
import Indicator from "@/components/shared/Indicator";
import classNames from "classnames";

type PageQueryParamsType = IntegrationOrderSearchParams & {
  tab: string;
} & IntegrationLogSearchParams & {
    typeList?: any; // Duplicated field name
  };

const Config = {
  includePagination: true,
  defaultQuery: {
    type: withDefault(StringParam, TMSOrderType.Scan),
    tab: withDefault(StringParam, "order"),
  },
  defaultPagination: {
    ...getDefaultSorts(IntegrationOrderCols),
    ...getDefaultSorts(IntegrationLogCols),
    size: 20,
  },
};

export function IntegrationOrderPageFC() {
  const { hintPrompt } = useGlobalContext();
  const [query, , changeParams] = usePageQuery<PageQueryParamsType>(
    [
      ...IntegrationOrderPageSearchItems,
      ...IntegrationLogTableSearchItems,
    ] as SearchItem[],
    Config,
  );
  const debounced = useDebounce(query, 500);
  const checkTMSOrderModalRef = useRef<DialogRef>(null);
  const onSearchFormChange = useCallback(
    (key: string, value: unknown) => {
      changeParams((old) => ({ ...old, [key]: value, page: 1 }));
    },
    [changeParams],
  );

  const onSelectedTabChange = useCallback(
    (key: string) => {
      changeParams((old) => ({ ...old, tab: key, page: 1 }));
    },
    [changeParams],
  );

  const orderIDColumn = useMemo(() => {
    return {
      headerName: "RII No",
      field: "customerNumber",
      resizable: true,
      pinned: "left",
      minWidth: 200,
      maxWidth: 280,
      suppressMenu: true,
      rowDrag: false,
      cellRenderer: (row: ICellRendererParams<IntegrationOrderVo, string>) => (
        <LinkColumn
          row={row}
          isLink={false}
          getLink={() => "#"}
          onLinkButtonClick={() => {
            if (row.data) {
              if (row.data.type === IntegrationOrderType.Customer) {
                hintPrompt("warning", "Warning", "2C order not supported!");
                return;
              } else {
                checkTMSOrderModalRef.current?.showModal({
                  integrationOrderId: row.data.id,
                  type: row.data.type,
                });
              }
            }
          }}
        />
      ),
    } as ColDef<IntegrationOrderVo>;
  }, [hintPrompt]);

  const cols = useMemo(() => {
    return [...IntegrationOrderCols, orderIDColumn];
  }, [orderIDColumn]);

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title="Integration Orders"
        description=""
        className="shrink-0 pt-2 pl-2 pb-1"
      />
      <CommonTabs
        active={query.tab}
        tabs={IntegrationOrderPageTabs}
        className="px-2 pb-2"
        size="large"
        onTabChange={onSelectedTabChange}
      />
      {query.tab === "order" ? (
        <div className="flex flex-grow flex-col rounded-md border-[1px] border-gray-200">
          <SearchForm<PageQueryParamsType>
            items={IntegrationOrderPageSearchItems as SearchItem[]}
            className="bg-white global_inherit_top_radius shrink-0 border-b-[1px] border-gray-200"
            onFormChange={onSearchFormChange}
            values={query}
          />
          <IntegrationOrdersTable
            cols={cols}
            className="grow bg-white global_inherit_no_top_radius"
            query={debounced}
            onQueryChange={changeParams}
          />
        </div>
      ) : query.tab === "logs" ? (
        <div className="flex flex-grow flex-col rounded-md border-[1px] border-gray-200">
          <SearchForm<PageQueryParamsType>
            items={IntegrationLogTableSearchItems as SearchItem[]}
            className="bg-white global_inherit_top_radius shrink-0 border-b-[1px] border-gray-200"
            onFormChange={onSearchFormChange}
            values={query}
          />
          <IntegrationLogsTable
            cols={IntegrationLogCols}
            className="grow bg-white global_inherit_no_top_radius"
            query={debounced}
            onQueryChange={changeParams}
          />
        </div>
      ) : null}
      <CheckTMSScanOrderModal ref={checkTMSOrderModalRef} />
    </div>
  );
}

export const IntegrationOrderPage = wrapPage(IntegrationOrderPageFC);

interface CheckTMSScanOrderModalProps {
  className?: string;
}

const CheckTMSScanOrderModal = forwardRef<
  DialogRef,
  CheckTMSScanOrderModalProps
>((props, ref) => {
  const { className } = props;
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tmsOrders, setTmsOrders] = useState<TMSOrderVo[]>([]);

  const onCancel = useCallback(() => {
    setOpen(false);
  }, []);

  const closeDialog = useCallback(() => {
    setOpen(false);
    setTmsOrders([]);
    setLoading(false);
  }, []);

  const openDialog = useCallback(
    (props: unknown) => {
      setLoading(true);
      const { integrationOrderId } = props as {
        integrationOrderId: number;
      };
      getIntegrationOrderAssociatedTMSOrder(integrationOrderId)
        .then((orders) => {
          if (orders.length === 1) {
            closeDialog();
            setTimeout(() => {
              window.open(
                `/order/scan_process?riiOrderNo=${orders[0].riiOrderNo}`,
                "_blank",
              );
            }, 500);
          } else if (orders.length > 0) {
            setTmsOrders(orders);
          } else {
            setTmsOrders([]);
          }
        })
        .finally(() => {
          setLoading(false);
        });
      setOpen(true);
    },
    [closeDialog],
  );

  useImperativeHandle(
    ref,
    () => ({
      showModal: openDialog,
      hideModal: closeDialog,
    }),
    [openDialog, closeDialog],
  );
  return (
    <Modal
      title="Check RII Processing Order"
      open={open}
      onCancel={onCancel}
      maskClosable={false}
      footer={null}
      loading={loading}
    >
      <div className={classNames("flex flex-col mt-[-6px]", className)}>
        <Indicator
          label={
            tmsOrders.length > 0
              ? `${tmsOrders.length} RII processing orders found`
              : "No orders found"
          }
          hint={tmsOrders.length > 0 ? "(select one to go)" : ""}
        />
        <div
          className={classNames(
            "flex items-center gap-2 w-full",
            "border-[1px] border-gray-200 rounded-md",
          )}
        >
          {tmsOrders.length === 0 ? (
            <div className="flex items-center justify-center h-full w-full">
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
            </div>
          ) : (
            tmsOrders.map((order) => (
              <div
                className={classNames(
                  "w-full p-2 px-4 rounded-md border-b-[1px] border-gray-200",
                  "flex justify-between",
                  "flex items-center gap-2",
                  "last:border-b-0",
                )}
              >
                <span>{order.riiOrderNo}</span>
                <Button
                  key={order.no}
                  onClick={() => {
                    window.open(
                      `/order/scan_process?orderId=${order.id}`,
                      "_blank",
                    );
                  }}
                >
                  Open
                </Button>
              </div>
            ))
          )}
        </div>
      </div>
    </Modal>
  );
});
